import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { CommentComposer } from './CommentComposer'
import { CommentItem } from './CommentItem'
import { MemoAuthor } from '../ProjectCanvas/nodes/MemoAuthor'
import { groupCommentsByDay, type CommentEntry } from './commentDays'
import './TaskComments.scss'

/** So nah am Ende gilt die Liste noch als „unten" — dann folgt sie neuen Kommentaren. */
const BOTTOM_SLACK = 24

/** Die Schreibwege — an einer Aufgabe andere als an einem Projekt, die Liste kennt nur diese vier. */
export interface CommentThreadActions {
  create: (content: string) => Promise<unknown>
  update: (id: string, content: string) => Promise<void>
  remove: (id: string) => Promise<void>
  restore: (id: string) => Promise<void>
}

export interface CommentThreadProps {
  /** Die lebenden Kommentare, der älteste zuerst; `undefined`, solange sie laden. */
  comments: readonly CommentEntry[] | undefined
  actions: CommentThreadActions
  /** Der Satz, wenn es noch keinen Kommentar gibt. */
  emptyText: string
  /** Wessen Kommentare das sind — bei einem Wechsel steht die Liste wieder unten. */
  ownerKey: string
  /** Der angefangene Kommentar — er gehört dem Besitzer, nicht dem Reiter (siehe `TaskDetails`). */
  draft: string
  onDraftChange: (value: string) => void
  /** Escape im Eingabefeld: die Tastatur zurück an die Liste. */
  onReleaseFocus?: () => void
  /** Für die Story „Ändern": dieser Kommentar beginnt im Feld. */
  editingId?: string
}

/**
 * **Eine Kommentar-Liste** — an einer Aufgabe (#183) wie an einem Projekt (#184), damit beide gleich
 * aussehen und gleich bleiben. Eine weiße Karte über die ganze Höhe, darin die Kommentare nach Tagen,
 * der neueste unten — beim Öffnen steht die Liste unten. Das Eingabefeld ist der feste Fuß der Karte.
 * Löschen meldet sich mit Rückgängig.
 *
 * @example
 * <CommentThread comments={comments} actions={actions} emptyText={t('taskComments.empty')}
 *   ownerKey={task.id} draft={draft} onDraftChange={setDraft} onReleaseFocus={focusList} />
 */
export const CommentThread: React.FC<CommentThreadProps> = ({
  comments, actions, emptyText, ownerKey, draft, onDraftChange, onReleaseFocus, editingId,
}) => {
  const { t, i18n } = useTranslation()
  const listRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLTextAreaElement>(null)

  const days = useMemo(
    () => groupCommentsByDay(comments ?? [], new Date(), i18n.language, { today: t('taskComments.today'), yesterday: t('taskComments.yesterday') }),
    [comments, i18n.language, t],
  )

  /*
    **Unten angekommen** — beim Aufbau, beim Wechsel des Besitzers und nach dem eigenen Schicken.
    Ein Kommentar von einem anderen Gerät zieht die Liste nur mit, wenn sie schon unten stand:
    Wer weiter oben liest, soll nicht weggerissen werden.
  */
  const stickToBottom = useRef(true)
  useLayoutEffect(() => { stickToBottom.current = true }, [ownerKey])
  useLayoutEffect(() => {
    const list = listRef.current
    // Inaktiver Tab (display: none) misst nur Nullen (CLAUDE.md) — dann nichts anfassen.
    if (!list || list.clientHeight === 0 || !stickToBottom.current) return
    list.scrollTop = list.scrollHeight
  }, [comments, ownerKey])

  const handleScroll = useCallback(() => {
    const list = listRef.current
    if (!list || list.clientHeight === 0) return
    stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight <= BOTTOM_SLACK
  }, [])

  const submit = useCallback((content: string) => {
    stickToBottom.current = true
    onDraftChange('')
    void actions.create(content)
    fieldRef.current?.focus()
  }, [onDraftChange, actions])

  const edit = useCallback((id: string, content: string) => {
    void actions.update(id, content)
  }, [actions])

  const remove = useCallback(async (id: string) => {
    // Erst löschen, dann melden (ADR 0028): Der Schreibvorgang lässt die gemerkte Handlung
    // vergessen — danach gemeldet, bleibt ⌘Z beim Löschen.
    await actions.remove(id)
    showUndoToast({
      message: t('taskComments.deleted'),
      undoLabel: t('common.undo'),
      undoneMessage: t('taskComments.restored'),
      onUndo: () => actions.restore(id),
    })
  }, [actions, t])

  return (
    <section className="task-comments" aria-label={t('taskComments.list')}>
      <div className="task-comments__card">
        <div className="task-comments__list" ref={listRef} onScroll={handleScroll}>
          {comments && comments.length === 0 && <p className="task-comments__empty">{emptyText}</p>}
          {days.map(day => (
            <div key={day.key} className="task-comments__day">
              <h3 className="task-comments__day-label">{day.label}</h3>
              {/* Eine Folge: was eine Person am Stück geschrieben hat — die Pille einmal darüber (wie WhatsApp). */}
              {day.runs.map(run => (
                <div key={run.key} className="task-comments__run">
                  <div className="task-comments__author">
                    {/*
                      „Weiß" als Tokens: Die Hex-Werte der Memo-Palette stünden im Dunkeln hell in der dunklen
                      Karte. Halb zur Karte hin gemischt — reines `--muted` war neben den Blasen zu schwer.
                    */}
                    <MemoAuthor chipColor="color-mix(in srgb, var(--muted) 55%, var(--popover))" textColor="var(--foreground)" />
                  </div>
                  {run.comments.map(comment => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      onEdit={edit}
                      onDelete={id => void remove(id)}
                      initiallyEditing={comment.id === editingId}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
        <CommentComposer
          value={draft}
          onValueChange={onDraftChange}
          onSubmit={submit}
          onReleaseFocus={onReleaseFocus}
          textareaRef={fieldRef}
        />
      </div>
    </section>
  )
}
